import { PlacedLevel, LevelStatus } from '~/util/types/levels';
import { Tag } from '~/util/types/tags';
import { RecordLevelStatsRow } from '~/util/types/stats';

export type AredleMode = 'daily' | 'practice';

// Distance within which a guess is considered close instead of far.
export const AREDLE_THRESHOLDS = {
    position: 300,
    victors: 50,
    enjoyment: 15,
} as const;

// The fields the game compares against.
export interface AredleLevel {
    id: string;
    level_id: number;
    name: string;
    position: number;
    victors: number;
    enjoyment: number | null;
    tags: Tag[];
    two_player: boolean;
    status: LevelStatus;
}

export type ComparisonColor = 'correct' | 'close' | 'far' | 'neutral';
export type Direction = 'up' | 'down' | 'none';

export interface NumericComparison {
    color: ComparisonColor;
    direction: Direction;
}

// invert rank-style fields like placement
export function compareNumeric(
    guess: number | null,
    answer: number | null,
    threshold: number,
    invert = false,
): NumericComparison {
    if (guess == null || answer == null)
        return { color: 'neutral', direction: 'none' };
    if (guess === answer) return { color: 'correct', direction: 'none' };

    const diff = answer - guess;
    const answerIsHigher = invert ? diff < 0 : diff > 0;
    return {
        color: Math.abs(diff) <= threshold ? 'close' : 'far',
        direction: answerIsHigher ? 'up' : 'down',
    };
}

// Backgrounds and text colours
export const comparisonBgClass: Record<ComparisonColor, string> = {
    correct: 'bg-confirm',
    close: 'bg-amber-500',
    far: 'bg-destructive',
    neutral: 'bg-background-light',
};

export const comparisonTextClass: Record<ComparisonColor, string> = {
    correct: 'text-white',
    close: 'text-neutral-900',
    far: 'text-white',
    neutral: 'text-secondary-foreground',
};

// Merge the levels list with the record statistics
export function enrichLevels(
    levels: PlacedLevel[],
    stats: RecordLevelStatsRow[],
): AredleLevel[] {
    const victorsById = new Map<string, number>();
    for (const { level, records } of stats) {
        if (level) victorsById.set(level.id, records);
    }

    return levels
        .filter((level) => level.position != null)
        .map((level) => ({
            id: level.id,
            level_id: level.level_id,
            name: level.name,
            position: level.position,
            victors: victorsById.get(level.id) ?? 0,
            enjoyment: level.edel_enjoyment ?? null,
            tags: level.tags ?? [],
            two_player: level.two_player,
            status: level.status,
        }));
}

export interface PracticeFilters {
    includeLegacy?: boolean;
    rangeStart?: number | null;
    rangeEnd?: number | null;
}

export function filterPracticeLevels(
    levels: AredleLevel[],
    filters: PracticeFilters,
): AredleLevel[] {
    const { includeLegacy, rangeStart, rangeEnd } = filters;
    const [min, max] = [rangeStart ?? 1, rangeEnd ?? Infinity].sort(
        (a, b) => a - b,
    );

    return levels.filter(
        (level) =>
            (level.status === 'MainList' ||
                (level.status === 'Legacy' && includeLegacy)) &&
            level.position >= min &&
            level.position <= max,
    );
}

// The daily pool, being main list with 2-player included
export function dailyPool(levels: AredleLevel[]): AredleLevel[] {
    return levels.filter((level) => level.status === 'MainList');
}

// The instant the daily is frozen at, being the start of the given UTC day.
export function dailyAtTimestamp(dayKey: string): string {
    return `${dayKey}T00:00:00.000Z`;
}

// UTC day identifier
export function getDayKey(date: Date): string {
    return [
        date.getUTCFullYear(),
        String(date.getUTCMonth() + 1).padStart(2, '0'),
        String(date.getUTCDate()).padStart(2, '0'),
    ].join('-');
}

// UTC day key for day before the given date
export function getYesterdayKey(date: Date): string {
    const previous = new Date(date.getTime());
    previous.setUTCDate(previous.getUTCDate() - 1);
    return getDayKey(previous);
}

// Used to derive daily level from date
function hashString(value: string): number {
    let hash = 2166136261;
    for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
}

// Picks the day's answer by a deterministic "lottery" concept.
export function pickDailyLevel(
    levels: AredleLevel[],
    dayKey: string,
): AredleLevel | null {
    const pool = dailyPool(levels);
    if (pool.length === 0) return null;

    let winner = pool[0];
    let winningTicket = hashString(`${dayKey}:${winner.id}`);
    for (let i = 1; i < pool.length; i++) {
        const level = pool[i];
        const ticket = hashString(`${dayKey}:${level.id}`);
        if (ticket < winningTicket || (ticket === winningTicket && level.id < winner.id)) {
            winner = level;
            winningTicket = ticket;
        }
    }
    return winner;
}

export function pickRandomLevel(levels: AredleLevel[]): AredleLevel | null {
    if (levels.length === 0) return null;
    return levels[Math.floor(Math.random() * levels.length)];
}

// Emoji squares mirroring comparison colours
const comparisonEmoji: Record<ComparisonColor, string> = {
    correct: '🟩',
    close: '🟨',
    far: '🟥',
    neutral: '⬜',
};

// Keycap number emojis for the tag counts
const keycapDigits: string[] = [
    ...Array.from(
        { length: 10 },
        (_, i) => i + String.fromCodePoint(0xfe0f, 0x20e3),
    ),
    String.fromCodePoint(0x1f51f),
];

// Date format switch
function shortDateFromKey(dayKey: string): string {
    const [year, month, day] = dayKey.split('-').map(Number);
    return `${month}/${day}/${String(year).slice(-2)}`;
}

// The copy-paste recap of the finished round in chronological order.
export function buildSummary(
    mode: AredleMode,
    dayKey: string | null,
    guesses: AredleLevel[],
    answer: AredleLevel,
    won: boolean,
    gameUrl: string,
): string {
    const now = new Date();
    const dateLabel =
        mode === 'daily' && dayKey
            ? shortDateFromKey(dayKey)
            : `${now.getMonth() + 1}/${now.getDate()}/${String(
                  now.getFullYear(),
              ).slice(-2)}`;

    const modeLabel = mode === 'daily' ? 'Daily Challenge' : 'Practice';
    const header = won
        ? `AREDLE ${modeLabel} (${dateLabel}) in ${guesses.length}`
        : `AREDLE ${modeLabel} (${dateLabel}) - Gave up`;

    const row = (color: (guess: AredleLevel) => ComparisonColor) =>
        guesses.map((guess) => comparisonEmoji[color(guess)]).join('');

    const placement = row(
        (g) =>
            compareNumeric(
                g.position,
                answer.position,
                AREDLE_THRESHOLDS.position,
                true,
            ).color,
    );
    const victors = row(
        (g) =>
            compareNumeric(
                g.victors,
                answer.victors,
                AREDLE_THRESHOLDS.victors,
            ).color,
    );
    const enjoyment = row(
        (g) =>
            compareNumeric(
                g.enjoyment,
                answer.enjoyment,
                AREDLE_THRESHOLDS.enjoyment,
            ).color,
    );

    const answerTags = new Set(answer.tags);
    const tags = guesses
        .map((guess) => {
            if (guess.id === answer.id) return '🟩';
            const shared = guess.tags.filter((tag) =>
                answerTags.has(tag),
            ).length;
            return keycapDigits[shared] ?? String(shared);
        })
        .join('');

    return [
        header,
        '',
        `${placement} - Placement`,
        `${victors} - Victors`,
        `${enjoyment} - Enjoyment`,
        `${tags} - Tags`,
        '',
        gameUrl,
    ].join('\n');
}
